import React, { useCallback, useEffect, useState } from "react";
import { GlobalHeaderStateProvider, Hamburger, Logo, Navigation, useHeaderState } from "components";
import { useIsClient, useWindowSize } from "hooks";
import "./GlobalHeader.scss";
import classNames from "classnames";

const Header = () => {
  // this has been seperated out so that the menu state logic knows the current situation (it will break otherwise 😅)

  // get the state of the menu whether it is open or closed
  const {
    dispatch,
    state: { menuOpen },
  } = useHeaderState();

  const isClient = useIsClient();

  // closes the mobile menu if esc key is pressed
  const handleEscKey = useCallback(
    (event) => event.keyCode === 27 && menuOpen && dispatch({ type: "setMenuOpen", data: false }),
    [menuOpen, dispatch],
  );

  const size = useWindowSize();

  // closes the mobile menu if esc key is pressed
  useEffect(() => {
    if (isClient) {
      document.addEventListener("keydown", handleEscKey, false);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [handleEscKey, isClient]);

  // Check for window resize to see if menu needs to 'close' for desktop
  useEffect(() => {
    if (isClient && size.width > 1080 && menuOpen) {
      dispatch({
        type: "setMenuOpen",
        data: false,
      });
    }
  }, [isClient, size]);

  return (
    <>
      <Logo className="global-header__logo" />
      <div
        className={classNames({
          "global-header__nav": true,
          "global-header__nav--open": menuOpen,
        })}
      >
        <Navigation />
      </div>
      <Hamburger />
    </>
  );
};

interface HeaderInterface {
  isSticky?: boolean;
}

/**
 * The header and navigation to be displayed throughout the site
 *
 * @param {boolean} isSticky - whether the header should be sticky
 * @returns Returns the Header component to be used throughout the site
 *
 */
export const GlobalHeader = ({ isSticky }: HeaderInterface): JSX.Element => {
  const [isHomePage, setHomePage] = useState(false);

  useEffect(() => {
    if (useIsClient) {
      location.pathname !== "/" ? setHomePage(false) : setHomePage(true);
    }
  }, []);

  return (
    <header
      className={classNames({
        "layout--large": true,
        "global-header": true,
        "header--sticky": isSticky,
        "homepage-header": isHomePage,
      })}
    >
      <div className="global-header__wrapper">
        <GlobalHeaderStateProvider>
          <Header />
        </GlobalHeaderStateProvider>
      </div>
    </header>
  );
};
